import React from 'react';
import {
  Button, Card, Col, Divider, Empty, Input, Pagination, Row, Space, Tag, Typography
} from 'antd';
import {
  CalendarOutlined,
  EyeOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import QuickActionTimer from './QuickActionTimer';

const { Text, Title } = Typography;

export const ClassicGridViewPageVariant = ({
  filteredApplications,
  searchText,
  onSearchChange,
  pendingCount,
  pagination,
  onCardClick,
  now,
  bidView = 'new',
}) => {
  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }} gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Title level={4} style={{ margin: 0 }}>
            <ThunderboltOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            {bidView === 'submitted' ? 'My Bids' : 'New Requests'}
          </Title>
          <Text type="secondary" style={{ fontSize: '15px', marginTop: '4px', display: 'block' }}>
            {bidView === 'submitted'
              ? 'Track your bids, the current highest bid, and time remaining.'
              : 'Review inspections and directly accept or reject requests.'}
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Input
            placeholder="Search by ID or model name..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf', marginRight: '4px' }} />}
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ maxWidth: '400px', width: '100%', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}
          />
        </Col>
      </Row>

      {bidView === 'new' && pendingCount > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fff2e8 0%, #ffe7ba 100%)',
          border: '1px solid #ffd591',
          borderRadius: '12px',
          padding: '16px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(250, 140, 22, 0.05)',
          animation: 'bannerBorderGlow 3s infinite ease-in-out'
        }}>
          <Space size="middle">
            <span className="pulsing-warning-dot" style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#fa8c16',
              boxShadow: '0 0 8px rgba(250, 140, 22, 0.6)'
            }} />
            <Text strong style={{ color: '#d46b08', fontSize: '15px' }}>
              Action Needed: You have {pendingCount} pending inspections waiting for accept or reject.
            </Text>
          </Space>
        </div>
      )}

      {filteredApplications.length > 0 ? (
        <>
          <Row gutter={[24, 24]}>
            {filteredApplications.map(app => {
              const needsHigherBid = bidView === 'submitted'
                && app.bidAmount != null
                && app.bidAmount !== ''
                && Number.isFinite(Number(app.bidAmount))
                && Number(app.highestBid) > Number(app.bidAmount);
              const isHighestBid = bidView === 'submitted'
                && app.bidAmount != null
                && Number(app.bidAmount) > 0
                && Number(app.highestBid) === Number(app.bidAmount);
              return (
                <Col
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                  xl={6}
                  key={app.id}
                >
                  <Card
                    hoverable={Boolean(onCardClick)}
                    onClick={() => onCardClick?.(app.id)}
                    style={{
                      borderRadius: '12px',
                      border: needsHigherBid ? '2px solid #ff4d4f' : isHighestBid ? '2px solid #52c41a' : app.status === 'Pending' ? '1px solid #ffd591' : '1px solid #e8e8e8',
                      background: app.status === 'Pending' ? 'linear-gradient(180deg, #fffcf6 0%, #ffffff 100%)' : '#ffffff',
                      overflow: 'hidden',
                      height: '100%',
                      boxShadow: app.status === 'Pending' ? '0 4px 12px rgba(250,140,22,0.05)' : '0 2px 8px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    styles={{
                      body: {
                        padding: '20px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }
                    }}
                  >
                    <div>
                      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                        <Col>
                          <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>{app.id}</Text>
                        </Col>
                        <Col>
                          <Tag color={bidView === 'submitted' ? 'blue' : 'orange'}>
                            {bidView === 'submitted' ? 'Bidded' : 'New Request'}
                          </Tag>
                        </Col>
                      </Row>
                      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div style={{
                          width: '120px',
                          height: '90px',
                          background: '#fafafa',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          marginRight: '16px',
                          flexShrink: 0
                        }}>
                          <img
                            src={app.images?.[0]}
                            alt={app.deviceModel}
                            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>
                            {app.deviceModel}
                          </Title>

                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {bidView === 'submitted' && (
                              <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
                                Your Bid: {app.bidAmount != null && Number.isFinite(Number(app.bidAmount))
                                  ? `₹${Number(app.bidAmount).toLocaleString('en-IN')}`
                                  : 'Unavailable'}
                              </Text>
                            )}
                            <Text type="secondary" style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                              <UserOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} /> {app.customerName}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                              <CalendarOutlined style={{ marginRight: '6px', color: '#bfbfbf' }} /> {app.date}
                            </Text>

                          </Space>
                        </div>
                      </div>
                    </div>

                    <div>
                            {app.highestBid > 0 && (
                              <div style={{
                                background: needsHigherBid ? '#fff1f0' : isHighestBid ? '#f6ffed' : '#fff7e6',
                                border: needsHigherBid ? '1px solid #ff7875' : isHighestBid ? '1px solid #95de64' : '1px solid #ffd591',
                                borderRadius: '6px',
                                padding: '10px 12px',
                                marginBottom: '12px',
                                width: '100%',
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                              }}>
                                <Text style={{ color: isHighestBid ? '#389e0d' : '#8c4a00', display: 'block', fontSize: '11px', fontWeight: 700, lineHeight: 1 }}>
                                  HIGHEST BID
                                </Text>
                                <Text strong style={{ color: isHighestBid ? '#389e0d' : '#d46b08', display: 'block', fontSize: '16px', lineHeight: 1.2, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  ₹{app.highestBid.toLocaleString('en-IN')}
                                </Text>
                              </div>
                            )}
                      {needsHigherBid && (
                        <Text strong style={{ color: '#cf1322', display: 'block', marginBottom: '12px' }}>
                          Highest bid exceeds your bid. Increase your bid to lead.
                        </Text>
                      )}
                      {isHighestBid && (
                        <Text strong style={{ color: '#389e0d', display: 'block', marginBottom: '12px' }}>
                          Your bid is the highest bid.
                        </Text>
                      )}
                      <QuickActionTimer app={app} now={now} style={{ marginBottom: '12px' }} />
                      {onCardClick && <>
                      <Divider style={{ margin: '12px 0' }} />
                      <Button
                        type="primary"
                        ghost
                        block
                        icon={<EyeOutlined />}
                        style={{ borderRadius: '6px', height: '36px', fontWeight: 500 }}
                      >
                        View Details
                      </Button>

                      </>}
                    </div>

                  </Card>
                </Col>
              );
            })}
          </Row>
          {pagination?.total > pagination?.pageSize && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger={false}
                onChange={pagination.onChange}
              />
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '60px 0', background: '#fff', borderRadius: '12px', border: '1px dashed #d9d9d9', textAlign: 'center' }}>
          <Empty description={bidView === 'submitted' ? 'No submitted bids found' : 'No pending requests found'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
      <style>{`
        .device-preview-img {
          transition: transform 0.3s ease;
        }
        .device-preview-img:hover {
          transform: scale(1.05);
        }
        @keyframes bannerBorderGlow {
          0% {
            border-color: #ffd591;
            box-shadow: 0 4px 12px rgba(250, 140, 22, 0.05);
          }
          50% {
            border-color: #fa8c16;
            box-shadow: 0 4px 16px rgba(250, 140, 22, 0.15);
          }
          100% {
            border-color: #ffd591;
            box-shadow: 0 4px 12px rgba(250, 140, 22, 0.05);
          }
        }
        @keyframes tagPulse {
          0% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
            transform: scale(1.02);
          }
          100% {
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};
